import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Consumer Goods Industry Focus page — its copy and its figures, apart from
 * the component that draws them.
 *
 * **Why this cut has a page and the other sixteen do not.** `focus()` in
 * `content/reports.ts` writes every Industry Focus entry from one sentence
 * template, because nobody had supplied a sector's own words — that is what the
 * "Still to be supplied" amber note on all of them says. Consumer Goods is the
 * first one where somebody did: asked for on 2026-08-27, with the live
 * WordPress shortcode of digitopia.co's page pasted in full, two screenshots of
 * that page and five figures. So this is the second designed report page after
 * `content/daimi.ts`, and it is built from the same parts.
 *
 * **Unlike the DAIMI landing page, the copy here is not a transcription.** Every
 * paragraph and every bullet below is lifted verbatim out of the
 * `vc_column_text` blocks of the shortcode, punctuation included — the curly
 * apostrophe in "sector’s", the em dash in the methodology paragraph, and the
 * American spellings ("prioritizing", "industrializing") the original uses. That
 * is why almost nothing here carries a `check`: there is nothing to read back
 * against a screenshot. The two notes that remain are a numbers conflict and a
 * missing form, both real questions for a person rather than gaps in the
 * transcription; the third is about the artwork.
 *
 * **What the shortcode has that this page cannot.** Its five Download buttons
 * all point at `#download`, an anchor over an embedded HubSpot form
 * (portal 6887774, form c327e739-…). These documents are prerendered with no
 * client bundle at all (see `src/prerender.tsx`), so an embedded form script
 * would never run — and the other thirty reports on this site hand the reader
 * the PDF from our own bucket instead. So every Download button here is that
 * PDF, and `DAIMI_CG_DOWNLOAD.check` is what keeps the question of gating open.
 *
 * **Read back against a four-part capture of the live page on 2026-08-28**, in
 * the words the folded cuts arrived in — *"referans alarak ilgili sayfayı birebir
 * tıpa tıp aynısını tasarla"*, with the mockup, Figure 1 and two overlapping crops
 * of the cover attached again. Every paragraph, bullet, heading, caption and button
 * label below matched the capture word for word, which is what transcribing from
 * the shortcode rather than from a screenshot bought; the three figures were already
 * in `photo-src/` from the first request, at larger sizes than the files attached
 * this time, so nothing was re-attached. What the capture *did* settle is where the
 * live page is empty and this one is not — see `DAIMI_CG_INTRO.check` and
 * `DAIMI_CG_ANALYSIS.check`, the same two questions the Retail and Services cuts
 * carry — and `pages/DaimiConsumerGoods.tsx` folded onto `pages/FocusPage.tsx` with
 * it, so this page cannot drift from its five siblings again.
 */

/** The report this page belongs to — the branch `pages/Reports.tsx` takes on it. */
export const DAIMI_CG_SLUG = 'digital-and-ai-maturity-report-2026-consumer-goods-industry'

/**
 * The hero's three paragraphs, verbatim.
 *
 * The third is `FOCUS_CLOSE` word for word — the series' own closing line, which
 * is where `reports.ts` got it from in the first place. It is written out rather
 * than imported so this module reads as the page reads, and so that changing the
 * series line for the other sixteen cuts cannot silently reword a page whose
 * copy was supplied.
 */
export const DAIMI_CG_INTRO = {
  body: [
    'The consumer goods sector leads the cross-industry baseline with a score of 3.04. While the industry holds significant levers for value in pricing and supply chain, success depends on moving from isolated innovation pilots to full operational execution.',
    'To win in 2026, organizations must overcome data and resource constraints by prioritizing high-impact value streams and industrializing AI to drive faster, more accurate decision-making.',
    'This report outlines the sector’s maturity, leading strategies, and actions to accelerate transformation.',
  ],
  /**
   * The report cover, and the one image on this page that already existed.
   *
   * It was a 400px square — the size every report card in the grid carries — and
   * the file attached with this request is the same artwork at 2560px, so it
   * replaced it rather than landing beside it under a second name. The index card
   * reads the same key and simply gets a sharper cover out of it.
   */
  photo: {
    src: '/photo/daimi-report-2026-consumer-goods-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Consumer Goods Focus, February 2026',
  } satisfies Photo,
  check:
    'The live hero has no cover beside it — on the 2026-08-28 capture the right of that first screen is empty but for the ribbon. The cover is in the hero here because that is what every other designed report page on this site does, and because the 2560px file you sent would otherwise only ever be seen as a 400px index card. Say if the live emptiness was the point and it should come out. The two “scaled” crops attached with that capture were not used: they are overlapping crops of the same square artwork and the cover already held here is the larger, complete one.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The three bullets are one `vc_column_text` block on digitopia.co in which each
 * line opens with a bolded label — "Core Strengths:", "The Scaling Gap:",
 * "Industrializing AI:". Split into `label` and `body` here so the label can be
 * set rather than emphasised inside a sentence, which is what a `<strong>` in the
 * middle of a paragraph would give.
 */
export const DAIMI_CG_FINDINGS = {
  heading: 'DAIMI Findings',
  lede: 'Consumer Goods performance reveals a sector with steady progress but uneven depth, where a gap is widening between ambition and enterprise-scale execution.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Maturity is highest in operationally structured, measurable areas like supply chain execution, manufacturing efficiency, and core commercial routines.',
    },
    {
      label: 'The Scaling Gap',
      body: 'Momentum is often hindered by fragmented data and disconnected systems, leading to “local wins” that fail to compound into enterprise-wide value.',
    },
    {
      label: 'Industrializing AI',
      body: 'While many are stuck at the dashboard stage, advanced players are building repeatable AI engines for demand sensing, inventory optimization, and pricing effectiveness.',
    },
  ],
  photo: {
    src: '/photo/daimi-consumer-goods-index-benchmark.webp',
    alt: 'Consumer goods DAIMI scores 2020–2025: the average rising from 2.69 to 3.04, leaders from 3.10 to 3.78 and laggards from 1.75 to 2.12, over 3 to 106 assessments a year',
  } satisfies Photo,
  /** The figure's own caption, as the shortcode sets it under the image. */
  caption:
    'Figure 1: Digital & AI Maturity Index (DAIMI) Study Results: Consumer Goods Industry Score, Averages, Leaders, and Laggards',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
}

/** The analysis band, and the pair of covers beside it. */
export const DAIMI_CG_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Consumer Goods',
  body: [
    'To lead in 2026, consumer goods executives must prioritize a small set of high-value streams and transition from intuition-driven to data-driven decision routines. Success depends on strengthening data foundations and operationalizing AI in ways that directly improve planning accuracy and speed up decision-making across the value chain.',
    'Download the full report to access detailed DAIMI benchmarks and the strategic roadmap to turn operational discipline into a sustained digital advantage.',
  ],
  photo: {
    src: '/photo/daimi-report-2026-consumer-goods-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Consumer Goods Focus',
  } satisfies Photo,
  check:
    'On the live page this band is text only: the right half of that row is empty on the 2026-08-28 capture, and the report mockup you attached does not appear anywhere on it. It is beside the analysis here because that is where the other designed focus cuts put theirs and because the file would otherwise go unused. Say if the live band’s emptiness was deliberate.',
}

/*
 * The methodology and global-perspective bands used to be written out here, each
 * with its own transcription of copy that is the same on every focus cut. They
 * are `content/reportClosing.ts` now, drawn by `components/ReportClosing.tsx` on
 * every report page but three — asked for on 2026-08-28, *"her report sayfamızın
 * en altında bu tasarım bire bir aynı şekilde olmalı"*, and with it the white
 * export this band had been asking for: `DAIMI-white-1200x491`, the very file
 * digitopia.co puts here (image 9322). It is `daimi-framework-wheel-white.webp`
 * now, so the wheel sits straight on the dark band with no plate under it.
 *
 * That shared band is also what this page's last line of the 2026-08-28 request is
 * about — *"en aşağıda önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a
 * yönlendirelim"*. Nothing to change: `REPORT_GLOBAL.ctaHref` already resolves to
 * `DAIMI_SLUG`, which is `digital-ai-maturity-report-2026`, and the reference
 * capture agrees — its Global Perspective card names the 2026 report and calls this
 * cut "The Consumer Goods Focus Report", which is the `shortName` `focus()` already
 * gives it. There is no link to a 2024 edition anywhere on this page.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_CG_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Consumer Goods Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's two open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `daimiChecks()` next door uses, and appended to
 * the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: none of them is a missing file — this cut
 * arrived with its cover, both of its figures and its own words. Two are places
 * where the live page is empty and this one shows artwork that was supplied to it,
 * and one is a decision about gating that belongs to marketing.
 */
export function daimiConsumerGoodsChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_CG_INTRO.check },
    { section: 'analysis', check: DAIMI_CG_ANALYSIS.check },
    { section: 'download', check: DAIMI_CG_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_CG_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
